import { Select } from '@mantine/core'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import { translate } from 'i18n/i18n'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterId, TProperty } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getIrrigationZoneEmitterExternalVendorNames } from 'utils/getIrrigationZoneEmitterExternalVendorNames'
import type { TSelectItemWithExternalVendors } from 'utils/makeExternalVendorSelectProps'
import { makeExternalVendorSelectProps } from 'utils/makeExternalVendorSelectProps'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

type SelectDataItem = {
  group: string
  items: TSelectItemWithExternalVendors[]
}

export const DropdownSelectorIrrigationZone = () => {
  const { containerWidth } = useContext(PanelDetailsContext)
  const selectClickToggle = useSelectClickToggle()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const { irrigationZoneEmitter } = selectedFieldAssetsStore.useSelector((s) => ({
    irrigationZoneEmitter: s.irrigationZoneEmitter,
  }))

  const data = useMemo<SelectDataItem[]>(() => {
    return Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .flatMap((p: TProperty) => {
        if (!p.irrigationZoneEmitters) return []

        const uniqueZoneIds = [
          ...new Set(Object.values(p.irrigationZoneEmitters ?? {}).map((zone) => zone.irrigationZoneId)),
        ]

        const points = Object.values(p.points ?? {})

        return {
          group: p.propertyName,
          items: uniqueZoneIds
            .map((zoneId) => {
              const zoneEmitters = Object.values(p.irrigationZoneEmitters ?? {}).filter(
                ({ irrigationZoneId }) => irrigationZoneId === zoneId,
              )

              const externalVendorNames = [
                ...new Set(
                  zoneEmitters.flatMap((irrigationZoneEmitter) =>
                    getIrrigationZoneEmitterExternalVendorNames({ irrigationZoneEmitter, points }),
                  ),
                ),
              ]

              return {
                value: zoneId.toString(),
                label: zoneEmitters[0]?.name || `Zone ${zoneId}`,
                externalVendorNames,
              }
            })
            .sort(sortByKey('label')),
        }
      })
  }, [properties])

  if (!data.length) return null

  const selectedZoneId = irrigationZoneEmitter
    ? getIdAndEmitterTypeFromZoneEmitterTypeKey(irrigationZoneEmitter)?.irrigationZoneId
    : null

  const { selectProps, inputPaddingRight } = makeExternalVendorSelectProps({ data, value: selectedZoneId })

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        {...selectClickToggle}
        clearable={false}
        css={{
          width: containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3)),
          border: `1px solid ${colors.midnight}`,
          borderRadius: 3,
        }}
        data={data}
        {...selectProps}
        filter={mantineSelectSearchFilterProp}
        onChange={(newSelectedZoneId) => {
          if (newSelectedZoneId) {
            const properties = fieldAssetStore.getState()?.properties ?? {}
            const zoneId = Number(newSelectedZoneId)

            const propertyWithZone = Object.values(properties).find(
              (property) =>
                property?.irrigationZoneEmitters &&
                Object.values(property.irrigationZoneEmitters).some(
                  (zone) => zone.irrigationZoneId === zoneId,
                ),
            )

            const firstEmitterKey =
              propertyWithZone?.irrigationZoneEmitters &&
              (Object.entries(propertyWithZone.irrigationZoneEmitters).find(
                ([, zone]) => zone.irrigationZoneId === zoneId,
              )?.[0] as TIrrigationZoneEmitterId)

            if (firstEmitterKey) {
              setSelectedFieldAsset({ irrigationZoneEmitter: firstEmitterKey })
            }
          }
        }}
        placeholder={translate.phrases.banyanApp('Select an Irrigation Zone')}
        searchable
        size="xs"
        spellCheck="false"
        classNames={SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_CLASSNAMES}
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          ...SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_STYLES,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: {
            ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input,
            paddingRight: inputPaddingRight,
          },
        }}
        value={selectedZoneId}
        comboboxProps={{
          withinPortal: true,
          keepMounted: false,
          zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts,
        }}
      />
    </div>
  )
}
